import { FC, useEffect, useRef, useState } from "react";
import {
  InnerModalContainer,
  ModalContainer,
  RewardsSection,
  RewardsTitle,
  RewardImage,
  Flex,
  RewardsScroll,
  CloseIcon,
} from "./styles";
import { H4 } from "components/atoms/Heading";
import AvailableServices from "./AvailableServices";
import { MAX_MAP_HEIGHT, MAX_MAP_WIDTH } from "components/pages/GlobalRewards/constants";
import { CountryDetails, Reward } from "components/pages/GlobalRewards/types";

interface GlobalRewardsModal {
  countryDetails: CountryDetails;
  rewards: Reward[];
  closeModal: () => void;
  resetCountryModalOnChange: () => void;
  mapDimensions: { width: number; height: number };
}

export const noServices = {
  eap: { live: false, onRequest: false },
  vgp: { live: false, onRequest: false },
};

export const GlobalRewardsModal: FC<GlobalRewardsModal> = ({
  countryDetails: { country, services } = {},
  rewards,
  closeModal,
  resetCountryModalOnChange,
  mapDimensions,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showGradient, setShowGradient] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const currentRef = scrollRef?.current;

    if (currentRef) {
      setShowGradient(currentRef.scrollHeight > currentRef.clientHeight);
    }

    const handleScroll = () => {
      if (currentRef) {
        const { scrollTop, scrollHeight, clientHeight } = currentRef;

        const onePercentOfClientHeight = Math.floor(clientHeight * 0.01);
        const isAtBottom = scrollHeight - scrollTop <= clientHeight + onePercentOfClientHeight;

        setHasScrolled(true);
        setShowGradient(!isAtBottom);
      }
    };

    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  return (
    <ModalContainer
      // In-line styling is more performant than using styled components, in this case
      style={{
        transform: `scale(${Math.min(
          mapDimensions.width / MAX_MAP_WIDTH,
          mapDimensions.height / MAX_MAP_HEIGHT,
        )})`,
      }}
    >
      <InnerModalContainer>
        <div>
          <Flex>
            <H4>{country}</H4>
            <CloseIcon
              width={24}
              height={24}
              src="/static/icons/Close.svg"
              alt="Close icon"
              onClick={() => resetCountryModalOnChange()}
            />
          </Flex>
          <AvailableServices services={services || noServices} />
        </div>
        <RewardsSection>
          <RewardsTitle>Rewards in this country:</RewardsTitle>
          <RewardsScroll ref={scrollRef} $showGradient={showGradient} $hasScrolled={hasScrolled}>
            {rewards
              ? rewards.map(({ name, listItemImage }, index) => (
                  <RewardImage
                    width={120}
                    height={104}
                    key={name + index}
                    src={listItemImage}
                    alt={`${name} logo`}
                    priority
                  />
                ))
              : null}
          </RewardsScroll>
        </RewardsSection>
      </InnerModalContainer>
    </ModalContainer>
  );
};

export default GlobalRewardsModal;
